import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchCandidate360, type Candidate360 } from '../lib/userDetailService';
import { disableAccount, resetPassword } from '../lib/userService';
import { logger } from '../lib/logger';

const statusLabels: Record<string, string> = {
  active: 'Actif',
  pending_verification: 'En attente',
  pending_kyc: 'KYC en cours',
  suspended: 'Suspendu',
  rejected: 'Rejeté',
};
const statusClasses: Record<string, string> = {
  active: 'bg-blue-50 text-blue-700 border-blue-200',
  pending_verification: 'bg-amber-50 text-amber-700 border-amber-200',
  suspended: 'bg-red-50 text-red-700 border-red-200',
};

const videoStatusLabels: Record<string, string> = {
  uploading: 'Envoi', analyzing: 'Analyse IA', pending_review: 'À modérer',
  approved: 'Approuvée', rejected: 'Rejetée',
};
const appStatusLabels: Record<string, string> = {
  pending: 'En attente', viewed: 'Vue', selected: 'Sélectionné', rejected: 'Refusée',
};

function Card({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{label}</p>
      <p className="text-sm text-gray-800 mt-0.5">{value || '—'}</p>
    </div>
  );
}

const CandidateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [c, setC] = useState<Candidate360 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [newPwd, setNewPwd] = useState('');

  useEffect(() => {
    if (!id) return;
    fetchCandidate360(id)
      .then(setC)
      .catch((err) => {
        logger.error('load_candidate_360', 'CandidateDetail', { error: String(err) });
        setError('Candidat introuvable');
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleToggleSuspend = async () => {
    if (!c) return;
    setActionLoading(true);
    try {
      const willSuspend = c.status !== 'suspended';
      await disableAccount(c.userId, willSuspend);
      setC({ ...c, status: willSuspend ? 'suspended' : 'active' });
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPwd = async () => {
    if (!c || newPwd.length < 6) return;
    setActionLoading(true);
    try {
      await resetPassword(c.userId, newPwd);
      alert('Mot de passe réinitialisé.');
      setResetOpen(false);
      setNewPwd('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !c) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600 font-medium mb-4">{error || 'Erreur'}</p>
        <button onClick={() => navigate('/candidates')} className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] rounded-lg">
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* En-tête */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
        {c.photoUrl ? (
          <img src={c.photoUrl} alt="" className="w-16 h-16 rounded-full object-cover" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-[#365A8C] flex items-center justify-center text-white text-xl font-bold">
            {c.firstName?.[0] || ''}{c.lastName?.[0] || ''}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-gray-900">{c.firstName} {c.lastName}</h2>
            <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${statusClasses[c.status] || 'bg-gray-100 text-gray-500 border-gray-200'}`}>
              {statusLabels[c.status] || c.status}
            </span>
            {c.seedTag && <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-indigo-100 text-indigo-700 uppercase">seed</span>}
            {c.identityVerified && <span className="px-2 py-0.5 text-[11px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Identité vérifiée</span>}
          </div>
          {c.tagline && <p className="text-sm text-gray-500 mt-1 italic">« {c.tagline} »</p>}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
            <span>✉️ {c.email || '—'}</span>
            <span>📞 {c.phone || '—'}</span>
            <span>📍 {c.city || '—'}{c.department ? ` (${c.department})` : ''}</span>
            <span>Inscrit le {c.registeredAt}</span>
            <span>Dernière connexion : {c.lastLoginAt || 'jamais'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleToggleSuspend}
            disabled={actionLoading}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              c.status === 'suspended' ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-orange-600 bg-orange-50 hover:bg-orange-100'
            } disabled:opacity-40`}
          >
            {c.status === 'suspended' ? 'Réactiver' : 'Suspendre'}
          </button>
          <button
            onClick={() => { setResetOpen(true); setNewPwd(''); }}
            className="px-3 py-1.5 text-xs font-medium text-[#00244F] bg-blue-50 hover:bg-blue-100 rounded-md"
          >
            Réinit. mdp
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Vues de profil reçues', value: c.stats.profileViewsReceived, icon: '👁️' },
          { label: 'Favoris', value: c.stats.favoritesMade, icon: '⭐' },
          { label: 'Likes / Pass', value: c.stats.swipesMade, icon: '⚡' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3">
            <span className="text-2xl">{s.icon}</span>
            <div>
              <p className="text-xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Profil */}
        <Card title="Profil">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label="Profil complet" value={c.profileComplete ? 'Oui' : 'Non'} />
            <Field label="Permis de conduire" value={c.hasDrivingLicense ? 'Oui' : 'Non'} />
            <Field label="Mobilité" value={c.mobilityMinutes ? `${c.mobilityMinutes} min` : null} />
            <Field label="Rythme souhaité" value={c.desiredWorkRhythm} />
            <Field label="Contrats souhaités" value={c.desiredContractTypes?.join(', ')} />
            <Field
              label="Salaire souhaité"
              value={c.desiredSalaryMin || c.desiredSalaryMax ? `${c.desiredSalaryMin ?? '?'} – ${c.desiredSalaryMax ?? '?'} €` : null}
            />
            <Field label="Disponibilité" value={c.availabilityDate || c.availabilityType} />
            <Field
              label="Langues"
              value={c.languages.length ? c.languages.map((l) => `${l.language} (${l.level}/5)`).join(', ') : null}
            />
          </div>
        </Card>

        {/* Expériences */}
        <Card title={`Expériences (${c.experiences.length})`}>
          {c.experiences.length === 0 ? (
            <p className="text-sm text-gray-400">Aucune expérience renseignée</p>
          ) : (
            <div className="space-y-3">
              {c.experiences.map((e) => (
                <div key={e.id} className="border border-gray-100 rounded-lg px-3 py-2">
                  <p className="text-sm font-medium text-gray-800">{e.positionName || '—'}</p>
                  <p className="text-xs text-gray-500">
                    {[e.sectorName, e.companyName, e.city].filter(Boolean).join(' · ')}
                    {e.yearsExperience ? ` · ${e.yearsExperience} ans` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Vidéos */}
      <Card
        title={`Vidéos (${c.videos.length})`}
        action={
          <button onClick={() => navigate('/videos')} className="text-xs text-[#00244F] hover:underline">
            Ouvrir la modération →
          </button>
        }
      >
        {c.videos.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune vidéo</p>
        ) : (
          <div className="flex gap-3 flex-wrap">
            {c.videos.map((v) => (
              <div key={v.id} className="w-28">
                <div className="w-28 h-40 rounded-lg bg-gray-100 overflow-hidden relative">
                  {v.thumbnailUrl && <img src={v.thumbnailUrl} alt="" className="w-full h-full object-cover" />}
                  <span className="absolute bottom-1 left-1 right-1 text-center px-1 py-0.5 text-[10px] font-medium rounded bg-black/60 text-white">
                    {videoStatusLabels[v.status] || v.status}
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 text-center">{v.createdAt}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Candidatures */}
      <Card title={`Candidatures (${c.applications.length})`}>
        {c.applications.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune candidature</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                <th className="pb-2">Poste</th>
                <th className="pb-2">Établissement</th>
                <th className="pb-2">Ville</th>
                <th className="pb-2">Statut</th>
                <th className="pb-2">Date</th>
                <th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {c.applications.map((a) => (
                <tr key={a.id} className="border-t border-gray-100">
                  <td className="py-2 font-medium text-gray-800">{a.positionName}</td>
                  <td className="py-2 text-gray-600">{a.establishmentName}</td>
                  <td className="py-2 text-gray-600">{a.city}</td>
                  <td className="py-2">
                    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                      {appStatusLabels[a.status] || a.status}
                    </span>
                  </td>
                  <td className="py-2 text-gray-500">{a.appliedAt}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => navigate(`/applications/${a.id}`)} className="text-xs text-[#00244F] hover:underline">
                      Détail →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Modal reset password */}
      {resetOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setResetOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Réinitialiser le mot de passe</h3>
              <p className="text-sm text-gray-500 mt-1">{c.firstName} {c.lastName} ({c.email})</p>
            </div>
            <div className="p-6">
              <input
                type="text"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Nouveau mot de passe (min. 6 caractères)"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setResetOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg">
                Annuler
              </button>
              <button
                onClick={handleResetPwd}
                disabled={newPwd.length < 6 || actionLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg disabled:opacity-40"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateDetail;
